import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useKotCoachRound } from "../../hooks/useKotCoachRound";
import { useKotCoachSubmitResult } from "../../hooks/useKotCoachSubmitResult";
import {
  KotCoachStartRoundButton,
  KotCoachStopRoundButton,
} from "../../components/KotCoachStartRoundBanner";
import { KotCoachResultDialog } from "../../components/KotCoachResultDialog";
import {
  canCoachEnterResultForMatch,
  canCoachShowResultButton,
  canPlayerEnterResultForMatch,
  coachMustStopRoundBeforeResult,
  coachResultButtonLabelKey,
  normalizeKotMode,
} from "../../utils/kotCoachSubmit";
import {
  cameraScoreTableNumbers,
  isKotWaitingAfterSubmit,
  kotMatchesForCurrentRound,
  resolveKotViewerMatchForDisplay,
  isViewerKotLoneWaiting,
  findLonePlayerOnLastTableFromSnapshot,
  computeSubmissionSummary,
} from "../../utils/kotCoachRound";
import { findMyKotMatch } from "../../utils/trainingRoomAccess";
import {
  resolvePlayerDisplayFields,
  rosterLookup,
} from "../../utils/liveTrainingPlayerContext";
import { KotPlayerLeaderboard } from "./KotPlayerPanels";
import { LiveTrainingKotPlayerArena } from "./LiveTrainingKotPlayerArena";

function formatPlayerName(p, roster, playerId = p?.player_id) {
  const fields = resolvePlayerDisplayFields(p, roster, playerId);
  return (
    fields.name ||
    (playerId || p?.player_id ? String(playerId || p.player_id).slice(-6) : "—")
  );
}

function matchStateLabel(state, t) {
  if (state === "WON") return t("kotMatchWon") || "Done";
  if (state === "IN_PROGRESS") return t("kotMatchInProgress");
  return state || "—";
}

/**
 * Mobile KoT controls for live training — coach (all tables + stop) and player (full-screen arena).
 */
export function LiveTrainingKotPanel({
  socket,
  tsId,
  teId,
  exerciseStateId,
  snapshot,
  isCoachView,
  viewerPlayerId,
  roster = [],
  fullscreen = false,
  exercise = null,
  exerciseNumber = null,
  user = null,
  isLandscape = false,
  viewerHr = null,
  getHrSamples = null,
  /** Tables this phone films. Two or more turns it into the score pad for those tables. */
  scoreTableNumbers = null,
}) {
  const { t } = useTranslation();
  const [leaderboardOpen, setLeaderboardOpen] = useState(false);
  const cameraTables = cameraScoreTableNumbers(scoreTableNumbers);
  const isCameraScorekeeper = !isCoachView && cameraTables.length >= 2;

  const kotRound = useKotCoachRound({
    socket,
    teId,
    tsId,
    exerciseStateId,
    externalSnapshot: snapshot,
    enabled: Boolean(socket && tsId && teId),
    stopRoundMode: isCoachView ? "coach" : "player",
    viewerPlayerId: isCoachView ? null : viewerPlayerId,
  });

  const kotSubmit = useKotCoachSubmitResult({
    socket,
    tsId,
    teId,
    snapshot,
    enabled: Boolean(socket && tsId && teId),
    submitterMode: isCoachView
      ? "coach"
      : isCameraScorekeeper
        ? "tableCamera"
        : "player",
    viewerPlayerId: isCoachView ? null : viewerPlayerId,
    onSubmitted: () => {},
  });

  const roundState = snapshot?.current_round?.round_state ?? null;
  const kotMode = normalizeKotMode(
    snapshot?.config?.match_winner_mode || snapshot?.config?.kot_mode
  );
  const matches = useMemo(
    () => kotMatchesForCurrentRound(snapshot),
    [snapshot]
  );
  const leaderboard = snapshot?.leaderboard || [];
  const kotMatches = snapshot?.matches || [];
  const playersById = useMemo(() => {
    const map = new Map();
    (leaderboard || []).forEach((row) => {
      if (row?.player_id) map.set(String(row.player_id), row);
    });
    matches.forEach((m) => {
      if (m.player_a_id && m.player_a) map.set(String(m.player_a_id), m.player_a);
      if (m.player_b_id && m.player_b) map.set(String(m.player_b_id), m.player_b);
    });
    return map;
  }, [leaderboard, matches]);

  const myMatch = useMemo(() => {
    if (isCoachView) return null;
    return (
      resolveKotViewerMatchForDisplay(snapshot, viewerPlayerId) ||
      findMyKotMatch(snapshot, viewerPlayerId)
    );
  }, [isCoachView, snapshot, viewerPlayerId]);

  const loneWaiting = useMemo(
    () =>
      !isCoachView &&
      isViewerKotLoneWaiting(snapshot, viewerPlayerId, roundState),
    [isCoachView, snapshot, viewerPlayerId, roundState]
  );

  const canPlayerEnterResult =
    !isCoachView &&
    myMatch &&
    canPlayerEnterResultForMatch({
      match: myMatch,
      config: snapshot?.config,
      roundState,
      viewerPlayerId,
    });

  const waitingAfterSubmit = useMemo(
    () =>
      !isCoachView &&
      isKotWaitingAfterSubmit(snapshot, myMatch, exerciseStateId),
    [isCoachView, snapshot, myMatch, exerciseStateId]
  );

  const showPlayerStopButton =
    !isCoachView &&
    myMatch &&
    !waitingAfterSubmit &&
    ((kotMode === "STOP_SCORE_LEAD" &&
      (kotRound.canRequestStopRound || canPlayerEnterResult)) ||
      (kotMode === "FULL_SET" && canPlayerEnterResult));

  const handlePlayerStop = useCallback(() => {
    if (!myMatch) return;
    if (
      kotMode === "STOP_SCORE_LEAD" &&
      roundState === "IN_PROGRESS" &&
      kotRound.canRequestStopRound
    ) {
      kotRound.requestStopRound();
      kotSubmit.openForMatch(myMatch, { roundStateOverride: "STOP_REQUESTED" });
      return;
    }
    if (canPlayerEnterResult) {
      kotSubmit.openForMatch(myMatch);
    }
  }, [
    myMatch,
    kotMode,
    roundState,
    kotRound,
    canPlayerEnterResult,
    kotSubmit,
  ]);

  useEffect(() => {
    if (isCoachView || isCameraScorekeeper || !myMatch) return;
    if (myMatch.match_state === "WON" || myMatch.match_state === "ABANDONED") {
      kotSubmit.close();
    }
  }, [
    isCoachView,
    isCameraScorekeeper,
    myMatch?.kot_match_id,
    myMatch?.match_state,
    kotSubmit.close,
  ]);

  useEffect(() => {
    if (
      isCoachView ||
      isCameraScorekeeper ||
      !canPlayerEnterResult ||
      !myMatch ||
      waitingAfterSubmit
    ) {
      return;
    }
    if (!kotSubmit.activeMatch) {
      kotSubmit.openForMatch(myMatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isCoachView,
    isCameraScorekeeper,
    canPlayerEnterResult,
    waitingAfterSubmit,
    myMatch?.kot_match_id,
  ]);

  const showStopRequestedBanner = roundState === "STOP_REQUESTED";

  const handleCameraTableScore = useCallback(
    (match) => {
      if (!match) return;
      const mustStop = coachMustStopRoundBeforeResult({
        config: snapshot?.config,
        roundState,
      });
      if (mustStop) {
        kotRound.requestStopRoundForMatch(match, (err) => {
          if (err) return;
          kotSubmit.openForMatch(match, {
            roundStateOverride: "STOP_REQUESTED",
          });
        });
        return;
      }
      kotSubmit.openForMatch(match);
    },
    [snapshot?.config, roundState, kotRound, kotSubmit]
  );

  const resultDialogProps = {
    open: Boolean(kotSubmit.activeMatch),
    match: kotSubmit.activeMatch,
    leaderboard,
    coach: kotSubmit,
    formatPlayerLabel: (player, playerId) => {
      const p = player || playersById.get(String(playerId));
      return formatPlayerName(p, roster, playerId);
    },
  };

  if (!isCoachView && fullscreen && isCameraScorekeeper) {
    const covered = new Set(cameraTables);
    const cameraMatches = matches.filter((m) =>
      covered.has(Number(m.table_number))
    );
    const summary = computeSubmissionSummary(cameraMatches);
    const matchByTable = new Map(
      cameraMatches.map((m) => [Number(m.table_number), m])
    );

    return (
      <>
        <section
          className="lt-kot lt-kot--scorekeeper"
          aria-label={t("kingOfTheTable")}
        >
          <div className="lt-kot__header">
            <EmojiEventsIcon className="lt-kot__header-icon" />
            <div>
              <h3 className="lt-kot__title">{t("kingOfTheTable")}</h3>
              {snapshot?.current_round?.round_number != null ? (
                <span className="lt-kot__round">
                  {t("kotCurrentRound")}: #{snapshot.current_round.round_number}
                </span>
              ) : null}
            </div>
          </div>

          <p className="lt-kot__scorekeeper-hint">{t("kotCameraScoreHint")}</p>

          {cameraMatches.length > 0 ? (
            <p className="lt-kot__scorekeeper-progress" role="status">
              {t("kotCoachScoreEntryProgress", {
                submitted: summary.submitted,
                total: summary.total,
                defaultValue: `${summary.submitted}/${summary.total} tables submitted`,
              })}
            </p>
          ) : null}

          {showStopRequestedBanner ? (
            <div className="lt-kot__stop-banner" role="status">
              <HourglassBottomIcon fontSize="small" />
              <span>{t("kotCameraScoreStopBanner")}</span>
            </div>
          ) : null}

          {kotRound.stopRoundError ? (
            <p className="lt-kot__error" role="alert">
              {t(kotRound.stopRoundError) || kotRound.stopRoundError}
            </p>
          ) : null}

          <div className="lt-kot__tables">
            {cameraTables.map((tableNumber) => {
              const m = matchByTable.get(tableNumber) || null;
              if (!m) {
                return (
                  <div key={`wait-${tableNumber}`} className="lt-kot__table-card">
                    <div className="lt-kot__table-head">
                      <strong>
                        {t("table_with_number", { number: tableNumber })}
                      </strong>
                    </div>
                    <p className="lt-kot__scorekeeper-wait">
                      {t("kotCameraScoreWaiting")}
                    </p>
                  </div>
                );
              }
              const pa =
                rosterLookup(roster, m.player_a_id) ||
                m.player_a ||
                playersById.get(String(m.player_a_id));
              const pb =
                rosterLookup(roster, m.player_b_id) ||
                m.player_b ||
                playersById.get(String(m.player_b_id));
              const isWon =
                m.match_state === "WON" || m.match_state === "ABANDONED";
              const mustStop = coachMustStopRoundBeforeResult({
                config: snapshot?.config,
                roundState,
              });
              const canEnter =
                !isWon &&
                (canCoachEnterResultForMatch({
                  match: m,
                  config: snapshot?.config,
                  roundState,
                }) ||
                  (mustStop &&
                    canCoachShowResultButton({ match: m, roundState })));
              const isSubmitting = kotSubmit.isMatchSubmitting?.(m.kot_match_id);
              const submitErr = kotSubmit.getMatchSubmitError?.(m.kot_match_id);
              const isBusy = Boolean(isSubmitting || kotRound.stoppingRound);
              return (
                <div
                  key={m.kot_match_id}
                  className={`lt-kot__table-card${
                    m.match_state === "WON" ? " lt-kot__table-card--done" : ""
                  }`}
                >
                  <div className="lt-kot__table-head">
                    <strong>
                      {t("table_with_number", { number: m.table_number ?? "—" })}
                    </strong>
                    <span className="lt-kot__table-state">
                      {matchStateLabel(m.match_state, t)}
                    </span>
                  </div>
                  <div className="lt-kot__table-players">
                    <div className="lt-kot__table-row">
                      <span>{formatPlayerName(pa, roster, m.player_a_id)}</span>
                      <span>{m.player_a_score ?? "–"}</span>
                    </div>
                    <div className="lt-kot__table-row">
                      <span>{formatPlayerName(pb, roster, m.player_b_id)}</span>
                      <span>{m.player_b_score ?? "–"}</span>
                    </div>
                  </div>
                  {m.match_state === "WON" ? (
                    <div className="lt-kot__table-won">
                      <CheckCircleIcon fontSize="inherit" />
                      +{m.points_awarded_winner} {t("points")}
                    </div>
                  ) : null}
                  {submitErr ? (
                    <p className="lt-kot__error" role="alert">
                      {t(submitErr) || submitErr}
                    </p>
                  ) : null}
                  {canEnter ? (
                    <button
                      type="button"
                      className="lt-kot__table-enter"
                      disabled={isBusy}
                      onClick={() => handleCameraTableScore(m)}
                    >
                      {isSubmitting
                        ? t("submitting") || "Submitting"
                        : t(coachResultButtonLabelKey({
                            config: snapshot?.config,
                            roundState,
                          }))}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>

          {leaderboard.length > 0 ? (
            <button
              type="button"
              className="lt-kot__lb-toggle"
              onClick={() => setLeaderboardOpen(true)}
            >
              <EmojiEventsIcon fontSize="small" />
              {t("kotLeaderboard")}
            </button>
          ) : null}
        </section>
        <KotCoachResultDialog {...resultDialogProps} />
        <KotPlayerLeaderboard
          open={leaderboardOpen}
          rows={leaderboard}
          matches={kotMatches}
          myPlayerId={viewerPlayerId}
          roster={roster}
          onDismiss={() => setLeaderboardOpen(false)}
        />
      </>
    );
  }

  if (!isCoachView && fullscreen) {
    return (
      <>
        <LiveTrainingKotPlayerArena
          match={myMatch}
          snapshot={snapshot}
          roster={roster}
          viewerPlayerId={viewerPlayerId}
          exerciseStateId={exerciseStateId}
          exercise={exercise}
          exerciseNumber={exerciseNumber}
          user={user}
          isLandscape={isLandscape}
          roundState={roundState}
          stoppingRound={kotRound.stoppingRound}
          onStop={handlePlayerStop}
          onOpenLeaderboard={() => setLeaderboardOpen(true)}
          showLeaderboardButton={leaderboard.length > 0}
          showStopButton={showPlayerStopButton}
          stopRoundError={kotRound.stopRoundError}
          showStopBanner={showStopRequestedBanner}
          getHrSamples={getHrSamples}
          viewerHr={viewerHr}
        />
        <KotCoachResultDialog {...resultDialogProps} />
        <KotPlayerLeaderboard
          open={leaderboardOpen}
          rows={leaderboard}
          matches={kotMatches}
          myPlayerId={viewerPlayerId}
          roster={roster}
          onDismiss={() => setLeaderboardOpen(false)}
        />
      </>
    );
  }

  return (
    <section className="lt-kot" aria-label={t("kingOfTheTable")}>
      <div className="lt-kot__header">
        <EmojiEventsIcon className="lt-kot__header-icon" />
        <div>
          <h3 className="lt-kot__title">{t("kingOfTheTable")}</h3>
          {snapshot?.current_round?.round_number != null ? (
            <span className="lt-kot__round">
              {t("kotCurrentRound")}: #{snapshot.current_round.round_number}
            </span>
          ) : null}
        </div>
      </div>

      {showStopRequestedBanner ? (
        <div className="lt-kot__stop-banner" role="status">
          <HourglassBottomIcon fontSize="small" />
          <span>
            {t("kotStopRequestedBanner") ||
              "Stop called — enter winner and scores for your table."}
          </span>
        </div>
      ) : null}

      <div className="lt-kot__actions">
        {isCoachView ? (
          <>
            <KotCoachStopRoundButton
              visible={kotRound.canRequestStopRound}
              stopping={kotRound.stoppingRound}
              onStop={kotRound.requestStopRound}
              className="lt-kot__stop-btn"
            />
            <KotCoachStartRoundButton
              canStartRound={kotRound.canStartRound}
              awaitingCoachStart={kotRound.awaitingCoachStart}
              preparingNextRound={kotRound.preparingNextRound}
              roundNumber={kotRound.roundNumber ?? null}
              starting={kotRound.startingRound}
              onStart={kotRound.startNextRound}
              className="lt-kot__start-btn"
            />
          </>
        ) : (
          <>
            {kotMode === "STOP_SCORE_LEAD" && showPlayerStopButton ? (
              <button
                type="button"
                className="lt-kot__stop-btn lt-kot__stop-btn--player"
                disabled={kotRound.stoppingRound}
                onClick={handlePlayerStop}
              >
                {kotRound.stoppingRound
                  ? t("kotCoachStoppingRound")
                  : t("kotPlayerStopRound")}
              </button>
            ) : null}
            {kotMode === "FULL_SET" && canPlayerEnterResult ? (
              <button
                type="button"
                className="lt-kot__submit-btn"
                onClick={handlePlayerStop}
              >
                {t("liveTrainingKoTSubmit")}
              </button>
            ) : null}
          </>
        )}
      </div>

      {kotRound.stopRoundError ? (
        <p className="lt-kot__error" role="alert">
          {t(kotRound.stopRoundError) || kotRound.stopRoundError}
        </p>
      ) : null}

      {isCoachView ? (
        <div className="lt-kot__tables">
          {matches.length === 0 ? (
            <p className="lt-kot__empty">{t("kotNoMatchesYet")}</p>
          ) : (
            matches.map((m) => {
              const pa =
                rosterLookup(roster, m.player_a_id) ||
                m.player_a ||
                playersById.get(String(m.player_a_id));
              const pb =
                rosterLookup(roster, m.player_b_id) ||
                m.player_b ||
                playersById.get(String(m.player_b_id));
              const canEnter = canCoachEnterResultForMatch({
                match: m,
                config: snapshot?.config,
                roundState,
              });
              return (
                <div
                  key={m.kot_match_id}
                  className={`lt-kot__table-card${
                    m.match_state === "WON" ? " lt-kot__table-card--done" : ""
                  }`}
                >
                  <div className="lt-kot__table-head">
                    <strong>
                      {t("table_with_number", { number: m.table_number ?? "—" })}
                    </strong>
                    <span className="lt-kot__table-state">
                      {matchStateLabel(m.match_state, t)}
                    </span>
                  </div>
                  <div className="lt-kot__table-players">
                    <div className="lt-kot__table-row">
                      <span>{formatPlayerName(pa, roster, m.player_a_id)}</span>
                      <span>{m.player_a_score ?? "–"}</span>
                    </div>
                    <div className="lt-kot__table-row">
                      <span>{formatPlayerName(pb, roster, m.player_b_id)}</span>
                      <span>{m.player_b_score ?? "–"}</span>
                    </div>
                  </div>
                  {m.match_state === "WON" ? (
                    <div className="lt-kot__table-won">
                      <CheckCircleIcon fontSize="inherit" />
                      +{m.points_awarded_winner} {t("points")}
                    </div>
                  ) : null}
                  {canEnter ? (
                    <button
                      type="button"
                      className="lt-kot__table-enter"
                      onClick={() => kotSubmit.openForMatch(m)}
                    >
                      {t("kotCoachEnterResult")}
                    </button>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      ) : loneWaiting ? (
        <div className="lt-kot__lone-wait" role="status">
          <span className="lt-kot__my-match-label">{t("table")}</span>
          <strong>
            {t("table_with_number", {
              number:
                findLonePlayerOnLastTableFromSnapshot(snapshot)?.table_number ??
                "—",
            })}
          </strong>
          <p>{t("kotLonePlayerWaitingTitle") || "Waiting for opponent"}</p>
          <p className="lt-kot__lone-wait-hint">
            {t("kotLonePlayerWaitingHint") ||
              "You win by default when the round ends and move up one table."}
          </p>
        </div>
      ) : myMatch ? (
        <div className="lt-kot__my-match">
          <span className="lt-kot__my-match-label">{t("table")}</span>
          <strong>
            {t("table_with_number", { number: myMatch.table_number ?? "—" })}
          </strong>
          <div className="lt-kot__table-players">
            <div className="lt-kot__table-row">
              <span>
                {formatPlayerName(
                  rosterLookup(roster, myMatch.player_a_id) ||
                    myMatch.player_a ||
                    playersById.get(String(myMatch.player_a_id)),
                  roster,
                  myMatch.player_a_id
                )}
              </span>
              <span>{myMatch.player_a_score ?? "–"}</span>
            </div>
            <div className="lt-kot__table-row">
              <span>
                {formatPlayerName(
                  rosterLookup(roster, myMatch.player_b_id) ||
                    myMatch.player_b ||
                    playersById.get(String(myMatch.player_b_id)),
                  roster,
                  myMatch.player_b_id
                )}
              </span>
              <span>{myMatch.player_b_score ?? "–"}</span>
            </div>
          </div>
        </div>
      ) : null}

      {leaderboard.length > 0 ? (
        <button
          type="button"
          className="lt-kot__lb-toggle"
          onClick={() => setLeaderboardOpen(true)}
        >
          <EmojiEventsIcon fontSize="small" />
          {t("kotLeaderboard")}
        </button>
      ) : null}

      <KotCoachResultDialog {...resultDialogProps} />

      <KotPlayerLeaderboard
        open={leaderboardOpen}
        rows={leaderboard}
        matches={kotMatches}
        myPlayerId={viewerPlayerId}
        roster={roster}
        onDismiss={() => setLeaderboardOpen(false)}
      />
    </section>
  );
}

export default LiveTrainingKotPanel;
